import React, { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import axios from '@/utils/axiosConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Receipt, Plus, Trash2, Check, X, DollarSign, Send, Download } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const Invoices = () => {
  const { user, token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [files, setFiles] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    file_id: '',
    client_id: '',
    quote_id: '',
    description: '',
    honoraires: 0,
    debours: 0,
    items: [],
    tasks_items: [],  // Détails des honoraires professionnels
    debours_items: [],  // Détails des débours
    submit_for_approval: false
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Protection contre les mises à jour sur composant démonté
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    fetchInvoices();
    fetchClients();
    fetchFiles();
    fetchQuotes();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) {
        setInvoices(response.data);
      }
    } catch (error) {
      if (isMountedRef.current) {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) {
        setClients(response.data.filter(u => u.role === 'client'));
      }
    } catch (error) {
      console.error('Failed to fetch clients');
    }
  };

  const fetchFiles = async () => {
    try {
      const response = await axios.get(`${API}/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) {
        setFiles(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch files');
    }
  };

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) {
        setQuotes(response.data.filter(q => q.status === 'approuve'));
      }
    } catch (error) {
      console.error('Failed to fetch quotes');
    }
  };

  const loadQuoteData = (quoteId) => {
    if (quoteId === 'none') {
      // Reset to manual entry
      setFormData({
        ...formData,
        quote_id: '',
        description: '',
        honoraires: 0,
        debours: 0,
        items: [],
        tasks_items: [],
        debours_items: []
      });
      return;
    }
    
    const quote = quotes.find(q => q.id === quoteId);
    if (quote) {
      setFormData({
        ...formData,
        quote_id: quoteId,
        file_id: quote.file_id,
        client_id: quote.client_id,
        description: quote.description || '',
        honoraires: quote.honoraires || 0,
        debours: quote.debours || 0,
        items: quote.tasks_items || [],  // Deprecated - for backward compatibility
        tasks_items: quote.tasks_items || [],  // Honoraires breakdown
        debours_items: quote.debours_items || []  // Debours breakdown
      });
      toast.success('Informations du devis chargées avec détails');
    }
  };

  const calculateTotal = () => {
    const honoraires = parseFloat(formData.honoraires) || 0;
    const debours = parseFloat(formData.debours) || 0;
    const taxes = calculateTaxes(honoraires);
    return (parseFloat(taxes.subtotal_with_taxes) + debours).toFixed(2);
  };
  
  const calculateTaxes = (honoraires) => {
    const tps = honoraires * 0.05;
    const tvq = honoraires * 0.09975;
    return {
      tps: tps.toFixed(2),
      tvq: tvq.toFixed(2),
      total_taxes: (tps + tvq).toFixed(2),
      subtotal_with_taxes: (honoraires + tps + tvq).toFixed(2)
    };
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = field === 'description' ? value : parseFloat(value) || 0;
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = async (e, submitForApproval = false) => {
    e.preventDefault();
    try {
      const honoraires = parseFloat(formData.honoraires) || 0;
      const debours = parseFloat(formData.debours) || 0;
      
      await axios.post(
        `${API}/invoices`,
        {
          file_id: formData.file_id,
          client_id: formData.client_id,
          description: formData.description,
          honoraires: honoraires,
          debours: debours,
          items: formData.items,
          tasks_items: formData.tasks_items || [],  // CORRECTION: Inclure les tâches du devis
          debours_items: formData.debours_items || [],  // CORRECTION: Inclure les débours du devis
          submit_for_approval: submitForApproval
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(submitForApproval ? 'Facture créée et soumise pour approbation' : 'Facture créée avec succès');
      setDialogOpen(false);
      resetForm();
      fetchInvoices();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleApprove = async (invoiceId) => {
    try {
      await axios.put(
        `${API}/invoices/${invoiceId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Facture approuvée');
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleReject = async (invoiceId) => {
    const reason = prompt('Raison du refus:');
    if (!reason) return;

    try {
      await axios.put(
        `${API}/invoices/${invoiceId}/reject`,
        null,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { reason }
        }
      );
      toast.success('Facture refusée');
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleMarkAsPaid = async (invoiceId) => {
    try {
      await axios.put(
        `${API}/invoices/${invoiceId}/pay`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Facture marquée comme payée');
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette facture ?')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/invoices/${invoiceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Facture supprimée');
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      file_id: '',
      client_id: '',
      quote_id: '',
      description: '',
      honoraires: 0,
      debours: 0,
      items: [],
      tasks_items: [],
      debours_items: [],
      submit_for_approval: false
    });
  };

  const handleDownloadPDF = async (invoiceId) => {
    try {
      const response = await axios.get(
        `${API}/invoices/${invoiceId}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture_${invoiceId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      brouillon: <Badge className="bg-gray-100 text-gray-800">Brouillon</Badge>,
      en_attente_approbation: <Badge className="bg-yellow-100 text-yellow-800">En attente d'approbation</Badge>,
      approuve: <Badge className="bg-green-100 text-green-800">Approuvée</Badge>,
      refuse: <Badge className="bg-red-100 text-red-800">Refusée</Badge>,
      paye: <Badge className="bg-blue-100 text-blue-800">Payée</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  const getPaymentStatusBadge = (status) => {
    const badges = {
      en_attente: <Badge variant="outline" className="border-yellow-500 text-yellow-700">En attente</Badge>,
      paye: <Badge variant="outline" className="border-green-500 text-green-700">Payé</Badge>,
      annule: <Badge variant="outline" className="border-red-500 text-red-700">Annulé</Badge>
    };
    return badges[status] || <Badge variant="outline">{status}</Badge>;
  };

  const getInvoiceActions = (invoice) => {
    const isAdmin = user?.role === 'admin';
    const isEmployee = user?.role === 'employee';
    const isClient = user?.role === 'client';
    const isOwner = invoice.employee_id === user?.id;

    return (
      <div className="flex flex-wrap gap-2 mt-4">
        {/* Admin actions on pending invoices */}
        {isAdmin && invoice.status === 'en_attente_approbation' && (
          <>
            <Button
              onClick={() => handleApprove(invoice.id)}
              className="bg-green-600 hover:bg-green-700"
              size="sm"
            >
              <Check className="mr-2 h-4 w-4" />
              Approuver
            </Button>
            <Button
              onClick={() => handleReject(invoice.id)}
              variant="destructive"
              size="sm"
            >
              <X className="mr-2 h-4 w-4" />
              Refuser
            </Button>
          </>
        )}

        {/* Mark as paid - admin or client can do this */}
        {(isAdmin || isClient) && invoice.status === 'approuve' && invoice.payment_status === 'en_attente' && (
          <Button
            onClick={() => handleMarkAsPaid(invoice.id)}
            className="bg-blue-600 hover:bg-blue-700"
            size="sm"
          >
            <DollarSign className="mr-2 h-4 w-4" />
            Marquer comme payée
          </Button>
        )}

        {/* Delete button - Admin can delete any, Employee can delete own drafts */}
        {(isAdmin || (isEmployee && isOwner && invoice.status === 'brouillon')) && (
          <Button
            onClick={() => handleDelete(invoice.id)}
            variant="destructive"
            size="sm"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Supprimer
          </Button>
        )}

        {/* Download PDF button - visible for all users */}
        <Button
          onClick={() => handleDownloadPDF(invoice.id)}
          variant="outline"
          className="bg-blue-50"
          size="sm"
        >
          <Download className="mr-2 h-4 w-4" />
          Télécharger PDF
        </Button>
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="invoices-container">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-2">Factures</h1>
            <p className="text-lg text-gray-600">{invoices.length} factures</p>
          </div>

          {(user?.role === 'admin' || user?.role === 'employee') && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-red-600 to-blue-600" data-testid="create-invoice-btn">
                  <Plus className="mr-2 h-5 w-5" />
                  Créer une facture
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Nouvelle facture</DialogTitle>
                </DialogHeader>
                <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-6">
                  {/* SÉLECTEUR DE DEVIS */}
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-lg p-4">
                    <div className="space-y-2">
                      <Label className="text-purple-900 font-bold">📋 Charger depuis un devis approuvé (optionnel)</Label>
                      <Select value={formData.quote_id || 'none'} onValueChange={loadQuoteData}>
                        <SelectTrigger className="border-purple-300">
                          <SelectValue placeholder="Sélectionner un devis pour charger les informations" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Aucun devis (saisie manuelle)</SelectItem>
                          {quotes.map((quote) => (
                            <SelectItem key={quote.id} value={quote.id}>
                              Devis #{quote.id.substring(0, 8)} - {quote.client_name} - {quote.honoraires}$ + {quote.debours}$
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-sm text-purple-700">
                        💡 Sélectionnez un devis approuvé pour charger automatiquement les honoraires, débours et tâches
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Dossier *</Label>
                      <Select value={formData.file_id} onValueChange={(value) => {
                        const selectedFile = files.find(f => f.id === value);
                        if (selectedFile) {
                          setFormData({ ...formData, file_id: value, client_id: selectedFile.client_id });
                        }
                      }}>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner un dossier" />
                        </SelectTrigger>
                        <SelectContent>
                          {files.map((file) => (
                            <SelectItem key={file.id} value={file.id}>
                              {file.file_number} - {file.client_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Description générale</Label>
                      <Input
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Services d'immigration"
                      />
                    </div>
                  </div>

                  {/* HONORAIRES PROFESSIONNELS (TAXÉS) */}
                  <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                    <h3 className="text-lg font-bold text-blue-900 mb-4">💼 Honoraires Professionnels (Taxés)</h3>
                    <div className="space-y-2">
                      <Label>Montant des honoraires ($ CAD) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.honoraires}
                        onChange={(e) => setFormData({ ...formData, honoraires: e.target.value })}
                        placeholder="2000.00"
                        required
                      />
                    </div>
                  </div>

                  {/* DÉBOURS (NON TAXÉS) */}
                  <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50">
                    <h3 className="text-lg font-bold text-green-900 mb-4">📄 Débours (Non Taxés)</h3>
                    <div className="space-y-2">
                      <Label>Montant des débours ($ CAD)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.debours}
                        onChange={(e) => setFormData({ ...formData, debours: e.target.value })}
                        placeholder="500.00"
                      />
                      <p className="text-sm text-gray-600">Frais gouvernementaux, traductions, etc. (non soumis aux taxes)</p>
                    </div>
                  </div>

                  {/* CALCUL DU TOTAL */}
                  <div className="bg-gradient-to-r from-red-50 to-blue-50 p-6 rounded-lg">
                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Honoraires professionnels:</span>
                        <span className="font-semibold">${(parseFloat(formData.honoraires) || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">TPS (5%):</span>
                        <span className="font-semibold">${calculateTaxes(parseFloat(formData.honoraires) || 0).tps}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">TVQ (9.975%):</span>
                        <span className="font-semibold">${calculateTaxes(parseFloat(formData.honoraires) || 0).tvq}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm border-t pt-2">
                        <span className="text-gray-600">Sous-total avec taxes:</span>
                        <span className="font-semibold">${calculateTaxes(parseFloat(formData.honoraires) || 0).subtotal_with_taxes}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Débours (non taxés):</span>
                        <span className="font-semibold">${(parseFloat(formData.debours) || 0).toFixed(2)}</span>
                      </div>
                      <div className="border-t-2 pt-2 mt-2 flex justify-between items-center">
                        <span className="text-2xl font-bold">TOTAL À PAYER:</span>
                        <span className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-blue-600">
                          ${calculateTotal()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="submit"
                      variant="outline"
                      className="flex-1"
                      data-testid="save-draft-invoice-btn"
                    >
                      Enregistrer comme brouillon
                    </Button>
                    <Button
                      type="button"
                      onClick={(e) => handleSubmit(e, true)}
                      className="flex-1 bg-gradient-to-r from-red-600 to-blue-600"
                      data-testid="submit-invoice-btn"
                    >
                      <Send className="mr-2 h-4 w-4" />
                      {user?.role === 'admin' ? 'Créer et approuver' : 'Créer et soumettre'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="grid grid-cols-1 gap-6">
          {invoices.map((invoice) => (
            <Card key={invoice.id} className="border-0 shadow-xl" data-testid={`invoice-card-${invoice.id}`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-xl mb-2">
                      Facture #{invoice.id.substring(0, 8)}
                    </CardTitle>
                    <p className="text-gray-600">{invoice.description}</p>
                  </div>
                  <div>
                    <div className="flex gap-2 justify-end mb-2">
                      {getStatusBadge(invoice.status)}
                      {getPaymentStatusBadge(invoice.payment_status)}
                    </div>
                    {/* Display tax breakdown if available */}
                    {invoice.subtotal !== undefined && invoice.tps_amount !== undefined ? (
                      <div className="text-right space-y-1 text-sm mb-2">
                        <div className="text-gray-600">
                          Honoraires: ${invoice.subtotal?.toFixed(2)}
                        </div>
                        <div className="text-gray-600">
                          TPS: ${invoice.tps_amount?.toFixed(2)} | TVQ: ${invoice.tvq_amount?.toFixed(2)}
                        </div>
                      </div>
                    ) : null}
                    <p className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-blue-600">
                      Total: ${invoice.amount?.toFixed(2)}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Créée le:</span>
                      <span className="ml-2 font-medium">{format(new Date(invoice.created_at), 'dd/MM/yyyy')}</span>
                    </div>
                    {invoice.paid_at && (
                      <div>
                        <span className="text-gray-600">Payée le:</span>
                        <span className="ml-2 font-medium">{format(new Date(invoice.paid_at), 'dd/MM/yyyy')}</span>
                      </div>
                    )}
                  </div>

                  {invoice.items && invoice.items.length > 0 && (
                    <div className="border-t pt-4">
                      <p className="font-semibold mb-3">Articles:</p>
                      {invoice.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-2">
                          <span>{item.description} (x{item.quantity})</span>
                          <span className="font-medium">${(item.quantity * item.unit_price).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {getInvoiceActions(invoice)}
                </div>
              </CardContent>
            </Card>
          ))}

          {invoices.length === 0 && (
            <Card className="border-0 shadow-xl">
              <CardContent className="p-12 text-center text-gray-500">
                <Receipt className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Aucune facture</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Invoices;
